import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, mono = false }) => (
  <div className="col-md-4">
    <strong>{label}</strong>
    <br />
    <span style={mono ? { fontFamily: "monospace" } : undefined}>{value || "—"}</span>
  </div>
);

export default function ViewVendor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [activeTab, setActiveTab] = useState("addresses");

  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) {
      toast.error("You must be logged in.");
      navigate("/");
      return;
    }
    // if (!roles.includes("ROLE_ADMIN")) {
    //   toast.error("Unauthorized.");
    //   navigate("/");
    // }
  }, [navigate, token]);

  useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
    }, [token]);

  useEffect(() => {
    const loadVendor = async () => {
      try {
        const directRes = await fetch(`${API_BASE_URL}/api/vendors/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (directRes.ok) {
          const data = await directRes.json();
          setVendor(data);
          return;
        }

        const listRes = await fetch(`${API_BASE_URL}/api/vendors`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!listRes.ok) throw new Error("Failed to load vendor.");
        const listData = await listRes.json();
        const vendors = Array.isArray(listData) ? listData : listData.content || [];
        const foundVendor = vendors.find((item) => String(item.id) === String(id));
        if (!foundVendor) throw new Error("Vendor not found.");
        setVendor(foundVendor);
      } catch (err) {
        toast.error(err.message || "Failed to load vendor.");
        navigate("/vendors");
      } finally {
        setLoading(false);
      }
    };

    if (token && id) loadVendor();
  }, [id, navigate, token]);

  if (loading) {
    return (
      <>
        <div className="fixed-top">
          <AssetModuleNavbar />
        </div>
        <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
          Loading...
        </div>
      </>
    );
  }

  if (!vendor) return null;

  return (
    <>
      <div className="fixed-top">
        <AssetModuleNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="border-bottom pb-2 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <i className="bi bi-buildings fs-3 me-2 text-primary"></i>
              <h5 className="m-0 me-3">Vendor</h5>
            </div>
            <div className="d-flex align-items-center text-muted">
              <span className="me-2" style={{ cursor: "pointer", color: "blue" }} onClick={() => navigate("/vendors")}>
                List
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center mb-2">
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: "#eaf0f8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600,
                color: "#3a6090",
                marginRight: 12,
                flexShrink: 0,
              }}
            >
              {(vendor.name || "V").slice(0, 2).toUpperCase()}
            </div>
            <h4 className="m-0 me-3">{vendor.name || "Vendor"}</h4>
          </div>

          <div className="d-flex align-items-center mb-2">
            <button className="btn btn-primary me-2" onClick={() => navigate(`/editvendor/${id}`)}>
              Edit
            </button>
            <button className="btn btn-secondary btn-sm me-2" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>

        <Section title="Basic Information">
          <Field label="Vendor Name" value={vendor.name} />
          <Field label="Contact Person" value={vendor.contactPerson} />
          <Field label="Vendor Type" value={vendor.vendorType} />
          <Field label="Industry" value={vendor.industry} />
          <Field label="Website" value={vendor.website ? <a href={vendor.website} target="_blank" rel="noreferrer">{vendor.website}</a> : null} />
          <Field label="GST Number" value={vendor.gstNumber} mono />
          <Field label="Status" value={vendor.status} />
        </Section>

        <Section title="Contact Details">
          <Field label="Phone" value={vendor.phone} />
          <Field label="Email" value={vendor.email} />
          <Field label="Created At" value={vendor.createdAt ? new Date(vendor.createdAt).toLocaleString("en-IN") : "—"} />
        </Section>

        <ul className="nav nav-tabs mb-0 fw-semibold mt-3" style={{ backgroundColor: navbarColor }}>
          {[["addresses", "Address Details"], ["financial", "Financial"], ["systemNotes", "System Notes"]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div className="border p-3">
          {activeTab === "addresses" && (
            <Section title="Address Details">
              {(Array.isArray(vendor.addresses) && vendor.addresses.length > 0) ? vendor.addresses.map((addr, idx) => (
                <div key={idx} className="col-12 border rounded p-0 mb-2 overflow-hidden">
                  <div className="px-3 py-2 d-flex align-items-center gap-2" style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #dee2e6" }}>
                    <span className="badge bg-secondary">{addr.addressType || "Billing"}</span>
                    <span className="text-muted small">Address {idx + 1}</span>
                  </div>
                  <div className="row g-2 p-3">
                    <div className="col-md-12"><strong>Address Line 1:</strong> {addr.addressLine1 || "—"}</div>
                    {addr.addressLine2 && <div className="col-md-12"><strong>Address Line 2:</strong> {addr.addressLine2}</div>}
                    <div className="col-md-3"><strong>City:</strong> {addr.city || "—"}</div>
                    <div className="col-md-3"><strong>State:</strong> {addr.state || "—"}</div>
                    <div className="col-md-3"><strong>Country:</strong> {addr.country || "—"}</div>
                    <div className="col-md-3"><strong>Postal Code:</strong> {addr.postalCode || "—"}</div>
                  </div>
                </div>
              )) : (
                <div className="text-muted">No addresses on record.</div>
              )}
            </Section>
          )}
          {activeTab === "financial" && (
            <Section title="Financial">
              <Field label="Currency" value={vendor.currency} />
              <Field label="Payment Terms" value={vendor.paymentTerms} />
              <div className="col-12"><hr className="my-1" /><strong className="text-muted" style={{ fontSize: 13 }}>Bank / Payment Details</strong></div>
              <Field label="Bank Name" value={vendor.bankName} />
              <Field label="Account Holder Name" value={vendor.accountHolderName} />
              <Field label="Account Type" value={vendor.accountType} />
              <Field label="Account Number" value={vendor.accountNumber ? `•••• •••• ${vendor.accountNumber.slice(-4)}` : null} />
              <Field label="IFSC Code" value={vendor.ifscCode} mono />
              <Field label="SWIFT / BIC Code" value={vendor.swiftCode} mono />
              <Field label="Branch Name" value={vendor.branchName} />
            </Section>
          )}
          {activeTab === "systemNotes" && (
            <>
              <div className="text-muted small mb-2">
                <strong>Date Created:</strong>{" "}
                {vendor.createdAt ? new Date(vendor.createdAt).toLocaleString() : "—"}
              </div>
              <SystemNotes entityType="VENDOR" entityId={vendor.id} token={token} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
